'use client';

import { useCallback, useEffect, useRef } from 'react';
import { pushViewItemList } from '@/lib/analytics/dataLayer';
import type { GA4Item } from '@/lib/analytics/dataLayer';

interface UseGA4WidgetImpressionsOptions {
  items: GA4Item[];
  listId: string;
  listName: string;
}

interface UseGA4WidgetImpressionsResult {
  containerRef: (node: Element | null) => void;
}

const useGA4WidgetImpressions = ({
  items,
  listId,
  listName,
}: UseGA4WidgetImpressionsOptions): UseGA4WidgetImpressionsResult => {
  const firedRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Capture latest props via ref so the callback can be stable ([] deps).
  const propsRef = useRef({ items, listId, listName });

  useEffect(() => {
    propsRef.current = { items, listId, listName };
  });

  const containerRef = useCallback((node: Element | null) => {
    // Cleanup: disconnect previous observer (React calls with null on unmount,
    // or when containerRef identity changes).
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node || firedRef.current || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || firedRef.current) return;
        firedRef.current = true;
        observer.disconnect();
        observerRef.current = null;
        const { items: i, listId: id, listName: name } = propsRef.current;
        pushViewItemList({ item_list_id: id, item_list_name: name, items: i });
      },
      { threshold: 0.5 },
    );

    observer.observe(node);
    observerRef.current = observer;
  }, []); // stable — uses propsRef for latest values at fire time

  return { containerRef };
};

export default useGA4WidgetImpressions;
